// resources/js/Composables/useBookingSchedule.js
import { DateTime } from "luxon";

/**
 * The five states a slot can be in. The calendar has always computed all five, but the old
 * grid collapsed them into a mixed `number | "disabled" | object` return that each renderer
 * had to re-interpret. Both the desktop grid and the mobile list now read this instead, so
 * the two views cannot disagree about what a slot is.
 */
export const SLOT_FREE = "free";
export const SLOT_SOON = "soon";
export const SLOT_PAST = "past";
export const SLOT_BOOKED = "booked";
export const SLOT_EVENT = "event";

/** Bookings need an hour's notice. Kept as a named constant because the UI now labels it. */
export const NOTICE_HOURS = 1;

// Modulo 24 so a block ending at midnight formats as 12 AM rather than throwing on hour 24.
export const formatHour = (hour) => DateTime.now().set({ hour: hour % 24, minute: 0 }).toFormat("h a");

export const formatHourLong = (hour) => {
    const value = parseInt(hour) % 24;
    const meridiem = value >= 12 ? "PM" : "AM";
    return `${value % 12 || 12}:00 ${meridiem}`;
};

/**
 * Turns a date-specific override row into the same `{ time, visual }` slot shape the
 * OpeningTimes::slots accessor emits, so callers never care which source a day came from.
 */
export const slotsFromOverride = (override) => {
    if (override.is_closed || !override.opening_time || !override.closing_time) {
        return [];
    }

    // Times arrive as HH:mm:ss or HH:mm. Anything else is a serialization change we should
    // notice rather than silently render as a closed day.
    if (typeof override.opening_time !== "string" || typeof override.closing_time !== "string") {
        console.warn("Unexpected opening time format on override:", override);
        return [];
    }

    const openingHour = parseInt(override.opening_time.split(":")[0]);
    const closingHour = parseInt(override.closing_time.split(":")[0]);

    if (Number.isNaN(openingHour) || Number.isNaN(closingHour)) {
        console.warn("Unparseable opening times on override:", override);
        return [];
    }

    const slots = [];
    // The closing hour itself is not bookable; the last slot starts an hour before.
    const lastBookableHour = closingHour > 0 ? closingHour - 1 : 23;

    for (let hour = openingHour; hour <= lastBookableHour; hour++) {
        slots.push({ time: hour, visual: formatHour(hour) });
    }

    return slots;
};

export const overrideForDay = (day, overrides) => overrides?.[day.toFormat("yyyy-MM-dd")] || null;

/**
 * The bookable hours for any day, not just the one on screen — the day strip needs to know
 * whether next Tuesday is closed before you tap it.
 */
export const slotsForDay = (day, openingTimes, overrides) => {
    const override = overrideForDay(day, overrides);

    if (override) {
        return slotsFromOverride(override);
    }

    return openingTimes?.[day.toFormat("c")]?.slots || [];
};

/**
 * Walks one space's day and returns one cell per booking block, carrying the row span in
 * `length` the way the grid has always done. Cells are contiguous and cover every open hour.
 */
export const buildDaySchedule = ({ day, spaceView, events, hours }) => {
    if (!spaceView || !hours?.length) {
        return [];
    }

    const cells = [];
    const bookableFrom = DateTime.now().plus({ hours: NOTICE_HOURS });
    const spaceId = spaceView.space.id;
    const lastSlotHour = hours[hours.length - 1].time;

    for (let i = 0; i < hours.length; i++) {
        const hour = hours[i].time;
        const startsAt = day.set({ hour: hour, minute: 0 });
        const booking = spaceView.calendar?.[startsAt.toFormat("yyyy-MM-dd HH:00")];

        if (booking?.type === "event") {
            const event = events?.[spaceId]?.find((candidate) => candidate.event_id === booking.id);

            // An event on the calendar with no matching entry in the events payload used to
            // push a null the template then dereferenced. Treat it as a plain block instead.
            const length = clampToClosing(event?.hours ?? 1, hour, lastSlotHour);

            // A private event is someone else's booking: show it as a grey "Booked" block that
            // cannot be opened, the same as a table booking. An unmatched event is treated the same.
            if (!event || event.private) {
                cells.push({
                    kind: SLOT_BOOKED,
                    hour: hour,
                    length: length,
                    startsAt: startsAt,
                    name: "Booked",
                    eventId: null,
                });

                i += length - 1;
                continue;
            }

            cells.push({
                kind: SLOT_EVENT,
                hour: hour,
                length: length,
                startsAt: startsAt,
                name: event.name,
                eventId: event.event_id,
            });

            i += length - 1;
            continue;
        }

        if (booking) {
            const length = clampToClosing(booking.number_of_slots, hour, lastSlotHour);

            cells.push({
                kind: SLOT_BOOKED,
                hour: hour,
                length: length,
                startsAt: startsAt,
                name: "Booked",
                eventId: null,
            });

            i += length - 1;
            continue;
        }

        const kind = slotAvailability(startsAt, bookableFrom);
        const previous = cells[cells.length - 1];

        // The hour under way and the hour inside the notice window are unbookable for the same
        // reason - the clock - so they run together as one block. Left separate, the current
        // hour drew as an empty cell wedged between the finished morning and the explanation
        // for why you cannot book yet, which reads as a gap rather than a state.
        if (kind === SLOT_SOON && previous?.kind === SLOT_SOON) {
            previous.length += 1;
            continue;
        }

        cells.push({
            kind: kind,
            hour: hour,
            length: 1,
            startsAt: startsAt,
            name: null,
            eventId: null,
        });
    }

    return cells;
};

/**
 * Splits what the grid drew as one undifferentiated "disabled" hatch into the two things it
 * actually meant: the hour has gone, or it is inside the notice window. Same set of
 * unbookable hours either way.
 *
 * An hour counts as past only once it has finished. The one currently running cannot be booked
 * either, but it belongs with the notice window rather than with the morning - it is the near
 * edge of "not yet", not the far edge of "gone".
 */
const slotAvailability = (startsAt, bookableFrom) => {
    if (startsAt.plus({ hours: 1 }) <= DateTime.now()) {
        return SLOT_PAST;
    }

    return startsAt <= bookableFrom ? SLOT_SOON : SLOT_FREE;
};

/** A block never runs past the final slot of the day, however long it claims to be. */
const clampToClosing = (length, hour, lastSlotHour) => Math.max(1, Math.min(length, lastSlotHour - hour + 1));

export const isBookable = (cell) => cell.kind === SLOT_FREE;

/**
 * Where a day starts once the hours that have finished are dropped. An hour is gone only once
 * it has ended, the same test slotAvailability makes, so the hour under way still counts — it
 * is the near edge of "not yet", not the far edge of "gone". Returns hours.length when the
 * whole day is behind us, and 0 for any day that is not today.
 */
export const firstUnfinishedHourIndex = (day, hours) => {
    if (!hours?.length || !day.hasSame(DateTime.now(), "day")) {
        return 0;
    }

    const index = hours.findIndex(
        (hour) => day.set({ hour: hour.time, minute: 0 }).plus({ hours: 1 }) > DateTime.now(),
    );

    return index === -1 ? hours.length : index;
};

/**
 * The cells covering `fromHour` onwards, with a block that began earlier trimmed to the part
 * that is left rather than dropped. The calendar only carries a booking at the hour it starts,
 * so a block cut off at the top would leave the hours it still covers redrawing as free.
 * `startsAt` keeps the real start: it is what an event links to, not what the cell shows.
 */
export const cellsFromHour = (cells, fromHour) =>
    cells
        .filter((cell) => cell.hour + cell.length > fromHour)
        .map((cell) =>
            cell.hour >= fromHour
                ? cell
                : { ...cell, hour: fromHour, length: cell.hour + cell.length - fromHour },
        );

/**
 * How many unbroken free hours follow `startHour` on this table — the real ceiling on a
 * booking's length, since the next block or closing time is whatever stops it.
 */
export const freeRunFrom = ({ day, spaceView, events, hours, startHour }) => {
    let run = 0;

    for (const cell of buildDaySchedule({ day, spaceView, events, hours })) {
        if (cell.hour < startHour) {
            continue;
        }

        if (!isBookable(cell)) {
            break;
        }

        run += 1;
    }

    return run;
};

export const countFreeSlots = ({ day, spaceView, events, hours }) =>
    buildDaySchedule({ day, spaceView, events, hours }).filter(isBookable).length;

/** Free slots across every table on a day — what the day chips count. */
export const countFreeSlotsForDay = ({ day, calendarView, events, hours }) =>
    (calendarView || []).reduce(
        (total, spaceView) => total + countFreeSlots({ day, spaceView, events, hours }),
        0,
    );

/**
 * The day chips, for whichever strip is asking. Built here rather than in a component because
 * the phone and the desktop grid both need the same eight days described the same way — the
 * counts are the whole point of the strip, and they must not disagree between breakpoints.
 */
export const dayEntriesFor = ({ currentDay, calendarView, events, openingTimes, overrides }) =>
    bookableDays().map((day) => {
        const hours = slotsForDay(day, openingTimes, overrides);
        const closed = hours.length === 0;
        const free = closed ? 0 : countFreeSlotsForDay({ day, calendarView, events, hours });

        return {
            key: day.toISODate(),
            day: day,
            weekday: day.toFormat("ccc"),
            dayOfMonth: day.toFormat("d"),
            closed: closed,
            note: closed ? "Closed" : free === 0 ? "Full" : "Spaces available",
            selected: day.hasSame(currentDay, "day"),
            aria: `${day.toFormat("cccc d LLLL")}, ${closed ? "closed" : free === 0 ? "fully booked" : "spaces available"}`,
        };
    });

/** The next day we are actually open, so a closed day can offer somewhere to go. */
export const nextOpenDayAfter = (day, openingTimes, overrides) =>
    bookableDays().find(
        (candidate) => candidate > day && slotsForDay(candidate, openingTimes, overrides).length > 0,
    ) || null;

/** The day's opening range, as both views label it: "11:00 AM – 9:00 PM". */
export const openingLabel = (hours) =>
    hours?.length ? `${formatHourLong(hours[0].time)} – ${formatHourLong(hours[hours.length - 1].time + 1)}` : "";

/** Heading for a day whose hours were overridden — today reads differently from next Thursday. */
export const specialHoursHeading = (day) =>
    day.hasSame(DateTime.now(), "day") ? "Special hours today" : `Special hours on ${day.toFormat("cccc")}`;

/**
 * A table's shortest bookable length. Four places derived this from the brackets with the same
 * Math.min, and the booking sheet, the table summary and the details sheet all have to agree
 * about it or they contradict each other on screen.
 */
export const minimumHours = (space) =>
    !space?.brackets?.length ? 1 : Math.min(...space.brackets.map((bracket) => bracket.min));

/** The cheapest a table can be booked for, or null when it has no brackets to price it. */
export const priceHintFor = (space) =>
    space?.brackets?.length ? `from £${Math.min(...space.brackets.map((bracket) => bracket.price))}` : null;

/** One line of table: what it is, who fits, and any minimum worth knowing before picking a slot. */
export const spaceSummary = (space) => {
    if (!space) {
        return "";
    }

    const parts = [space.size, `seats up to ${space.seats}`];
    const minimum = minimumHours(space);

    if (minimum > 1) {
        parts.push(`${minimum} hr minimum`);
    }

    return parts.filter(Boolean).join(" · ");
};

/**
 * The bookable window, today through today + 7, matching the datepicker's min/max so the day
 * strip and the desktop picker offer exactly the same days.
 */
export const bookableDays = (span = 7) => {
    const today = DateTime.now().startOf("day");
    return Array.from({ length: span + 1 }, (_, offset) => today.plus({ days: offset }));
};
